import type { Tone } from "./StatusDot";

const FILL: Record<Tone, string> = {
  default: "bg-fg",
  muted: "bg-muted",
  accent: "bg-accent",
  money: "bg-money",
  business: "bg-business",
  ok: "bg-ok",
  warn: "bg-warn",
  danger: "bg-danger",
  info: "bg-info",
};

interface ProgressBarProps {
  /** 0..100. Values above 100 are clamped for the bar but shown as-is elsewhere. */
  value: number;
  tone?: Tone;
  height?: number;
  /** Optional "you should be here by now" tick (expected progress). */
  markerPct?: number | null;
  markerTitle?: string;
  className?: string;
}

export default function ProgressBar({
  value,
  tone = "accent",
  height = 6,
  markerPct = null,
  markerTitle,
  className = "",
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  return (
    <div
      className={`relative w-full overflow-hidden rounded-full bg-surface-2 ${className}`}
      style={{ height }}
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={`h-full rounded-full transition-[width] duration-500 ${FILL[tone]}`}
        style={{ width: `${clamped}%` }}
      />
      {markerPct !== null && markerPct > 0 && markerPct <= 100 && (
        <span
          title={markerTitle}
          className="absolute top-0 h-full w-px bg-line-strong"
          style={{ left: `${Math.min(100, markerPct)}%` }}
        />
      )}
    </div>
  );
}
